/**
 * Key detection using Krumhansl-Schmuckler pitch class profiles.
 *
 * Computes a pitch class histogram from MIDI notes, then correlates
 * against major and minor profiles to find the best-fitting key.
 */

export type RelativeKey = {
  tonic: string;
  mode: 'major' | 'minor';
  label: string;
};

export type DetectedKey = {
  /** Pitch class of the tonic (0–11) */
  tonic: number;
  /** 'major' or 'minor' */
  mode: 'major' | 'minor';
  /** Correlation score (higher = better fit) */
  confidence: number;
  /** Display label, e.g. "C major", "F# minor" */
  label: string;
  /** Relative key (sharing the same key signature). */
  relative?: RelativeKey;
};

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** Conventional spellings for major/minor tonics (matches analyzer-go). */
const TONIC_NAMES_MAJOR = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const TONIC_NAMES_MINOR = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B'];

/** Map of every note spelling (sharps and flats) to its pitch class 0-11. */
const TONIC_TO_PITCH_CLASS: Readonly<Record<string, number>> = {
  C: 0, 'C#': 1, Db: 1,
  D: 2, 'D#': 3, Eb: 3,
  E: 4, F: 5, 'F#': 6, Gb: 6,
  G: 7, 'G#': 8, Ab: 8,
  A: 9, 'A#': 10, Bb: 10,
  B: 11,
};

function tonicName(pitchClass: number, mode: 'major' | 'minor'): string {
  const pc = ((pitchClass % 12) + 12) % 12;
  const arr = mode === 'minor' ? TONIC_NAMES_MINOR : TONIC_NAMES_MAJOR;

  return arr[pc];
}

function relativePitchClass(pitchClass: number, mode: 'major' | 'minor'): { pc: number; mode: 'major' | 'minor' } {
  const pc = ((pitchClass % 12) + 12) % 12;

  if (mode === 'major') {
    return { pc: ((pc - 3) % 12 + 12) % 12, mode: 'minor' };
  }

  return { pc: (pc + 3) % 12, mode: 'major' };
}

export type KeySignature = {
  /** Number of accidentals in the armure (0 to 7). */
  count: number;
  /** Whether the accidentals are sharps, flats, or none (natural). */
  accidental: 'sharp' | 'flat' | 'natural';
};

/**
 * Key signatures (armures) for major keys, indexed by pitch class. Follows
 * the conventional spelling used by `tonicName` (e.g. pc 1 = Db not C#),
 * which is what the cycle of fifths assumes.
 */
const KEY_SIGNATURE_MAJOR: ReadonlyArray<KeySignature> = [
  { count: 0, accidental: 'natural' }, // C
  { count: 5, accidental: 'flat' },    // Db
  { count: 2, accidental: 'sharp' },   // D
  { count: 3, accidental: 'flat' },    // Eb
  { count: 4, accidental: 'sharp' },   // E
  { count: 1, accidental: 'flat' },    // F
  { count: 6, accidental: 'sharp' },   // F#
  { count: 1, accidental: 'sharp' },   // G
  { count: 4, accidental: 'flat' },    // Ab
  { count: 3, accidental: 'sharp' },   // A
  { count: 2, accidental: 'flat' },    // Bb
  { count: 5, accidental: 'sharp' },   // B
];

/**
 * Key signatures for minor keys, indexed by pitch class. Follows the
 * conventional spelling used by `tonicName` (e.g. pc 1 = C# not Db).
 */
const KEY_SIGNATURE_MINOR: ReadonlyArray<KeySignature> = [
  { count: 3, accidental: 'flat' },    // C minor
  { count: 4, accidental: 'sharp' },   // C# minor
  { count: 1, accidental: 'flat' },    // D minor
  { count: 6, accidental: 'flat' },    // Eb minor
  { count: 1, accidental: 'sharp' },   // E minor
  { count: 4, accidental: 'flat' },    // F minor
  { count: 3, accidental: 'sharp' },   // F# minor
  { count: 2, accidental: 'flat' },    // G minor
  { count: 5, accidental: 'sharp' },   // G# minor
  { count: 0, accidental: 'natural' }, // A minor
  { count: 5, accidental: 'flat' },    // Bb minor
  { count: 2, accidental: 'sharp' },   // B minor
];

/**
 * Returns the armure (key signature) for a given tonic and mode: how many
 * sharps or flats appear at the start of a staff for that key. Useful to
 * surface the practical reading cost of a transposition.
 */
export function keySignature(tonic: string, mode: 'major' | 'minor'): KeySignature | null {
  const pc = TONIC_TO_PITCH_CLASS[tonic];

  if (pc === undefined) {
    return null;
  }

  return mode === 'minor' ? KEY_SIGNATURE_MINOR[pc] : KEY_SIGNATURE_MAJOR[pc];
}

/**
 * Transposes a tonic by N semitones, returning the conventional spelling for
 * the target plus its relative key. Returns null if the input tonic is not
 * a recognised note name.
 */
export function transposeKey(
  tonic: string,
  mode: 'major' | 'minor',
  semitones: number,
): { tonic: string; mode: 'major' | 'minor'; label: string; relative: RelativeKey } | null {
  const pc = TONIC_TO_PITCH_CLASS[tonic];

  if (pc === undefined) {
    return null;
  }

  const newPc = (((pc + semitones) % 12) + 12) % 12;
  const newTonic = tonicName(newPc, mode);
  const rel = relativePitchClass(newPc, mode);
  const relTonic = tonicName(rel.pc, rel.mode);

  return {
    tonic: newTonic,
    mode,
    label: `${newTonic} ${mode}`,
    relative: {
      tonic: relTonic,
      mode: rel.mode,
      label: `${relTonic} ${rel.mode}`,
    },
  };
}

// Krumhansl-Schmuckler key profiles (empirical weights per scale degree)
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

/**
 * Detect the key of a piece from an array of MIDI note numbers.
 * Returns null if too few notes to be meaningful.
 */
export function detectKey(midiNotes: number[]): DetectedKey | null {
  if (midiNotes.length < 10) return null;

  // Build pitch class histogram
  const histogram = new Float64Array(12);
  for (const midi of midiNotes) {
    histogram[midi % 12]++;
  }

  // Normalize
  const total = histogram.reduce((s, v) => s + v, 0);
  if (total === 0) return null;
  for (let i = 0; i < 12; i++) histogram[i] /= total;

  let best: DetectedKey | null = null;

  for (let tonic = 0; tonic < 12; tonic++) {
    for (const mode of ['major', 'minor'] as const) {
      const profile = mode === 'major' ? MAJOR_PROFILE : MINOR_PROFILE;
      const r = correlate(histogram, profile, tonic);

      if (!best || r > best.confidence) {
        best = {
          tonic,
          mode,
          confidence: r,
          label: NOTE_NAMES[tonic] + ' ' + mode,
        };
      }
    }
  }

  return best;
}

/** Pearson correlation between histogram and profile rotated by tonic. */
function correlate(histogram: Float64Array, profile: number[], tonic: number): number {
  let sumH = 0, sumP = 0;
  for (let i = 0; i < 12; i++) {
    sumH += histogram[i];
    sumP += profile[i];
  }
  const meanH = sumH / 12;
  const meanP = sumP / 12;

  let num = 0, denH = 0, denP = 0;
  for (let i = 0; i < 12; i++) {
    const h = histogram[(i + tonic) % 12] - meanH;
    const p = profile[i] - meanP;
    num += h * p;
    denH += h * h;
    denP += p * p;
  }

  const den = Math.sqrt(denH * denP);
  return den === 0 ? 0 : num / den;
}

/**
 * Returns the set of diatonic pitch classes for a given key.
 * Useful for resolving ambiguities in chord detection.
 */
export function diatonicPitchClasses(tonic: number, mode: 'major' | 'minor'): Set<number> {
  const steps = mode === 'major'
    ? [0, 2, 4, 5, 7, 9, 11]
    : [0, 2, 3, 5, 7, 8, 10]; // natural minor
  return new Set(steps.map(s => (tonic + s) % 12));
}
